import { assertClaudeValueLoopScopes, type ClaudeValueLoopScope } from './claude-value-loop.js';
import type { PaginatedResponse } from './api-envelope.js';

export const CONNECTION_KINDS = [
  'CLI',
  'LOCAL_MCP',
  'REMOTE_MCP',
  'IDE',
  'DIRECT_API',
  'AUTOMATION',
  'IMPORT',
  'LEGACY_AGENT',
] as const;
export type ConnectionKind = (typeof CONNECTION_KINDS)[number];

export const CONNECTION_LIFECYCLE_STATUSES = ['PENDING', 'ENABLED', 'DISABLED', 'REVOKED'] as const;
export type ConnectionLifecycleStatus = (typeof CONNECTION_LIFECYCLE_STATUSES)[number];

export const CONNECTION_AUTH_MODES = [
  'OAUTH',
  'INSTALLATION_TOKEN',
  'WORKLOAD_IDENTITY',
  'SERVICE_CREDENTIAL',
  'NONE',
] as const;
export type ConnectionAuthMode = (typeof CONNECTION_AUTH_MODES)[number];

export interface ConnectionMemberResponse {
  id: string;
  workspaceId: number;
  name: string;
  kind: ConnectionKind;
  lifecycleStatus: ConnectionLifecycleStatus;
  authMode: ConnectionAuthMode;
  environment: string | null;
}

export interface ConnectionListParams {
  cursor?: string;
  limit?: number;
  kind?: ConnectionKind;
  lifecycleStatus?: ConnectionLifecycleStatus;
}

export type ConnectionListResponse = PaginatedResponse<ConnectionMemberResponse, string>;

/**
 * Phase 3D.0A's remote OAuth beta surface is intentionally distinct from
 * the general Connection member catalogue and from the broad legacy OAuth
 * profiles. These values are closed wire vocabulary.
 */
export const CONNECTION_TRUST_VIEW_STATES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'REVOKED',
  'UNAVAILABLE',
] as const;
export type ConnectionTrustViewState = (typeof CONNECTION_TRUST_VIEW_STATES)[number];

export const CONNECTION_CURRENT_USER_AUTHORITY_STATES = [
  'NOT_AUTHORISED',
  'AUTHORISED',
  'EXPIRED',
  'REVOKED',
  'UNAVAILABLE',
] as const;
export type ConnectionCurrentUserAuthority =
  (typeof CONNECTION_CURRENT_USER_AUTHORITY_STATES)[number];

export const CONNECTION_BETA_ALLOWED_TRANSITIONS = [
  'APPROVE',
  'REJECT',
  'ENABLE',
  'DISABLE',
  'REVOKE',
] as const;
export type ConnectionAllowedTransition = (typeof CONNECTION_BETA_ALLOWED_TRANSITIONS)[number];

export const CONNECTION_BETA_ERRORS = [
  'connection_binding_not_eligible',
  'connection_binding_provenance_invalid',
  'connection_binding_authorization_denied',
  'connection_binding_conflict',
  'connection_binding_state_conflict',
  'connection_authority_unavailable',
  'connection_not_found',
] as const;
export type ConnectionBetaError = (typeof CONNECTION_BETA_ERRORS)[number];

export const CONNECTION_BETA_LIST_ENVELOPE = ['data', 'meta'] as const;
export const CONNECTION_BETA_VIEW_FIELDS = [
  'connection',
  'trust',
  'capability',
  'currentUserAuthority',
  'allowedTransitions',
  'version',
  'instructions',
] as const;
export const CONNECTION_BETA_VIEW_FORBIDDEN_FIELDS = [
  'approvedByOwnerId',
  'autoApprovedSource',
  'grant',
  'token',
  'audit',
  'profile',
  'epoch',
  'lastObservedAt',
  'verifiedAt',
] as const;

export type OAuthConnectionBindingResponse = {
  id: string;
  clientId: string;
  workspaceId: number;
  connectionId: string;
  connectionKind: 'REMOTE_MCP';
  connectionAuthMode: 'OAUTH';
  createdAt: string;
};

export type OAuthConnectionApprovalResponse = {
  binding: OAuthConnectionBindingResponse;
  connection: ConnectionMemberResponse;
  trust: { clientId: string; workspaceId: number; state: 'approved' };
};

export type ConnectionBetaViewResponse = {
  connection: {
    id: string;
    name: string;
    kind: 'REMOTE_MCP';
    environment: string | null;
    lifecycleStatus: 'PENDING' | 'ENABLED' | 'DISABLED' | 'REVOKED';
  };
  trust: {
    clientId: string;
    clientName: string;
    state: ConnectionTrustViewState;
    registrationEvidence: 'PRE_REGISTERED_BETA_CLIENT';
    redirectUris: string[];
    requestedAt: string | null;
  };
  capability: 'REMOTE_MCP_OAUTH_BETA_SKILLS_READ_V1' | 'REMOTE_MCP_VERSIONED_SKILLS_V1';
  requestedScopes?: ClaudeValueLoopScope[];
  consentRevision?: 1;
  enabled?: boolean;
  writesEnabled?: boolean;
  currentUserAuthority: ConnectionCurrentUserAuthority;
  allowedTransitions: ConnectionAllowedTransition[];
  version: number;
  instructions: { serverUrl: string; clientName: string };
};

export type ConnectionBetaListResponse = {
  data: ConnectionBetaViewResponse[];
  meta: { page: number; pageSize: number; total: number };
};

// Phase 4 beta-console contracts are additive to the frozen Phase 3D view.
export const CONNECTION_BETA_CONSOLE_MUTATION_OUTCOMES = [
  'APPROVED',
  'REJECTED',
  'ENABLED',
  'DISABLED',
  'REVOKED',
] as const;
export type ConnectionBetaMutationOutcome =
  (typeof CONNECTION_BETA_CONSOLE_MUTATION_OUTCOMES)[number];

export const CONNECTION_BETA_CAPABILITY = 'REMOTE_MCP_OAUTH_BETA_SKILLS_READ_V1' as const;
export const CONNECTION_BETA_CAPABILITY_COPY = 'Can read active workspace-visible Skills' as const;
export const CONNECTION_BETA_CONSOLE_ERRORS = [
  'connection_console_invalid_request',
  'connection_authority_rate_limited',
] as const;

export type ConnectionBetaAccessRequestResponse = {
  client: {
    id: string;
    name: string;
    registrationEvidence: 'PRE_REGISTERED_BETA_CLIENT';
    logoUrl: string | null;
    contact: string | null;
  };
  workspace: { id: number; name: string };
  capability: typeof CONNECTION_BETA_CAPABILITY | 'REMOTE_MCP_VERSIONED_SKILLS_V1';
  requestedScopes?: ClaudeValueLoopScope[];
  consentRevision?: 1;
  redirectUris: string[];
  requestedAt: string;
  allowedTransitions: ['APPROVE', 'REJECT'];
  version: 0;
};

export type ConnectionBetaAccessRequestListResponse = {
  data: ConnectionBetaAccessRequestResponse[];
  meta: { page: 1; pageSize: number; total: number };
};

export type ConnectionBetaSetupResponse = {
  clientName: string;
  serverUrl: string;
  capability: typeof CONNECTION_BETA_CAPABILITY | 'REMOTE_MCP_VERSIONED_SKILLS_V1';
  requestedScopes?: ClaudeValueLoopScope[];
  consentRevision?: 1;
  enabled?: boolean;
  writesEnabled?: boolean;
};

export type ConnectionBetaMutationResponse = {
  outcome: ConnectionBetaMutationOutcome;
  connectionId: string | null;
  version: number;
};

export type ConnectionBetaVersionRequest = { expectedVersion: number };
export type ConnectionValueLoopApprovalRequest = {
  expectedVersion: number;
  consentRevision: 1;
  confirmedScopes: ClaudeValueLoopScope[];
};
export type ConnectionBetaApprovalRequest =
  | ConnectionBetaVersionRequest
  | ConnectionValueLoopApprovalRequest;
export type ConnectionBetaRejectRequest = { expectedVersion: 0; reason: string };
export type ConnectionBetaRevokeRequest = { expectedVersion: number; acknowledged: true };
export type ConnectionBetaConsoleError = { statusCode: number; message: string; error: string };

const CONSOLE_ID_MAX = 512;

function consoleInvalid(): never {
  throw new Error('connection_console_invalid_request');
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) consoleInvalid();
  return value as Record<string, unknown>;
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): void {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) {
    consoleInvalid();
  }
}

function hasControlCharacter(value: string): boolean {
  return [...value].some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });
}

function safeDisplay(value: unknown, options: { nullable?: boolean } = {}): string | null {
  if (options.nullable && value === null) return null;
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value.length > CONSOLE_ID_MAX ||
    hasControlCharacter(value)
  ) {
    consoleInvalid();
  }
  return value;
}

function safeHttpsUrl(value: unknown): string | null {
  if (typeof value !== 'string' || hasControlCharacter(value)) return null;
  try {
    const parsed = new URL(value);
    if (
      parsed.protocol !== 'https:' ||
      parsed.username ||
      parsed.password ||
      parsed.hash ||
      value !== parsed.toString()
    ) {
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

function positiveSafeInteger(value: unknown, allowZero = false): number {
  if (!Number.isSafeInteger(value) || (allowZero ? Number(value) < 0 : Number(value) <= 0)) {
    consoleInvalid();
  }
  return Number(value);
}

export function createConnectionBetaAccessRequest(
  input: unknown
): ConnectionBetaAccessRequestResponse {
  const value = record(input);
  const client = record(value.client);
  const workspace = record(value.workspace);
  if (
    (value.capability !== CONNECTION_BETA_CAPABILITY &&
      value.capability !== 'REMOTE_MCP_VERSIONED_SKILLS_V1') ||
    value.version !== 0 ||
    !Array.isArray(value.allowedTransitions) ||
    value.allowedTransitions.length !== 2 ||
    value.allowedTransitions[0] !== 'APPROVE' ||
    value.allowedTransitions[1] !== 'REJECT'
  ) {
    consoleInvalid();
  }
  const successor = value.capability === 'REMOTE_MCP_VERSIONED_SKILLS_V1';
  if (successor) {
    if (value.consentRevision !== 1) consoleInvalid();
    assertClaudeValueLoopScopes(value.requestedScopes as string[]);
  }
  const clientId = safeDisplay(client.id) as string;
  const clientName = safeDisplay(client.name) as string;
  const workspaceId = positiveSafeInteger(workspace.id);
  const workspaceName = safeDisplay(workspace.name) as string;
  const requestedAt = safeDisplay(value.requestedAt) as string;
  if (!Number.isFinite(Date.parse(requestedAt))) consoleInvalid();
  const registered = Array.isArray(value.registeredRedirectUris)
    ? new Set(
        value.registeredRedirectUris.filter((uri): uri is string => safeHttpsUrl(uri) !== null)
      )
    : null;
  const redirectUris = Array.isArray(value.redirectUris)
    ? value.redirectUris.flatMap((uri) => {
        const safe = safeHttpsUrl(uri);
        return safe && (!registered || registered.has(safe)) ? [safe] : [];
      })
    : [];
  const safeLogo = safeHttpsUrl(client.logoUrl);
  const contact = client.contact === null ? null : (safeDisplay(client.contact) as string);
  return {
    client: {
      id: clientId,
      name: clientName,
      registrationEvidence: 'PRE_REGISTERED_BETA_CLIENT',
      logoUrl: safeLogo,
      contact,
    },
    workspace: { id: workspaceId, name: workspaceName },
    capability: successor ? 'REMOTE_MCP_VERSIONED_SKILLS_V1' : CONNECTION_BETA_CAPABILITY,
    ...(successor
      ? {
          requestedScopes: [...(value.requestedScopes as ClaudeValueLoopScope[])],
          consentRevision: 1 as const,
        }
      : {}),
    redirectUris,
    requestedAt,
    allowedTransitions: ['APPROVE', 'REJECT'],
    version: 0,
  };
}

export function createConnectionBetaAccessRequestList(
  input: unknown
): ConnectionBetaAccessRequestListResponse {
  const value = record(input);
  const meta = record(value.meta);
  const data = Array.isArray(value.data) ? value.data.map(createConnectionBetaAccessRequest) : [];
  const pageSize = positiveSafeInteger(meta.pageSize, true);
  const total = positiveSafeInteger(meta.total, true);
  if (meta.page !== 1) consoleInvalid();
  return { data, meta: { page: 1, pageSize, total } };
}

export function createConnectionBetaSetup(input: unknown): ConnectionBetaSetupResponse {
  const value = record(input);
  const successor = value.capability === 'REMOTE_MCP_VERSIONED_SKILLS_V1';
  if (!successor && value.capability !== CONNECTION_BETA_CAPABILITY) consoleInvalid();
  if (successor) assertClaudeValueLoopScopes(value.requestedScopes as string[]);
  const serverUrl = safeHttpsUrl(value.serverUrl);
  if (!serverUrl) consoleInvalid();
  return {
    clientName: safeDisplay(value.clientName) as string,
    serverUrl,
    capability: successor ? 'REMOTE_MCP_VERSIONED_SKILLS_V1' : CONNECTION_BETA_CAPABILITY,
    ...(successor
      ? {
          requestedScopes: value.requestedScopes as ClaudeValueLoopScope[],
          consentRevision: 1 as const,
          enabled: value.enabled === true,
          writesEnabled: value.writesEnabled === true,
        }
      : {}),
  };
}

export function createConnectionBetaMutationResponse(
  input: unknown
): ConnectionBetaMutationResponse {
  const value = record(input);
  if (!CONNECTION_BETA_CONSOLE_MUTATION_OUTCOMES.includes(value.outcome as never)) consoleInvalid();
  const connectionId =
    value.connectionId === null ? null : (safeDisplay(value.connectionId) as string);
  return {
    outcome: value.outcome as ConnectionBetaMutationOutcome,
    connectionId,
    version: positiveSafeInteger(value.version, true),
  };
}

export function parseConnectionBetaVersionRequest(input: unknown): ConnectionBetaVersionRequest {
  const value = record(input);
  exactKeys(value, ['expectedVersion']);
  return { expectedVersion: positiveSafeInteger(value.expectedVersion, true) };
}

export function parseConnectionValueLoopApprovalRequest(
  input: unknown
): ConnectionValueLoopApprovalRequest {
  const value = record(input);
  exactKeys(value, ['expectedVersion', 'consentRevision', 'confirmedScopes']);
  if (value.consentRevision !== 1) consoleInvalid();
  assertClaudeValueLoopScopes(value.confirmedScopes as string[]);
  return {
    expectedVersion: positiveSafeInteger(value.expectedVersion, true),
    consentRevision: 1,
    confirmedScopes: [...(value.confirmedScopes as ClaudeValueLoopScope[])],
  };
}

export function parseConnectionBetaRejectRequest(input: unknown): ConnectionBetaRejectRequest {
  const value = record(input);
  exactKeys(value, ['expectedVersion', 'reason']);
  if (value.expectedVersion !== 0 || typeof value.reason !== 'string') consoleInvalid();
  const reason = value.reason.trim();
  if (
    reason.length === 0 ||
    reason.length > 500 ||
    reason.includes('\u0000') ||
    reason.includes('\u007f')
  ) {
    consoleInvalid();
  }
  return { expectedVersion: 0, reason };
}

export function parseConnectionBetaRevokeRequest(input: unknown): ConnectionBetaRevokeRequest {
  const value = record(input);
  exactKeys(value, ['expectedVersion', 'acknowledged']);
  if (value.acknowledged !== true) consoleInvalid();
  return {
    expectedVersion: positiveSafeInteger(value.expectedVersion, true),
    acknowledged: true,
  };
}

export function createConnectionBetaConsoleError(input: unknown): ConnectionBetaConsoleError {
  const value = record(input);
  return {
    statusCode: Number(value.statusCode),
    message: String(value.message),
    error: String(value.error),
  };
}

type ConsolePrincipal =
  | 'OWNER'
  | 'ADMIN'
  | 'MEMBER'
  | 'AGENT_API_KEY'
  | 'NATIVE_INSTALLATION'
  | 'REMOVED_MEMBER'
  | 'SUSPENDED_USER'
  | 'UNJOINED_WORKSPACE'
  | 'FOREIGN_ID'
  | 'UNAUTHENTICATED';
type ConsoleResult = Record<ConsolePrincipal, number | 'N/A'>;
const consoleCollection: ConsoleResult = {
  OWNER: 200,
  ADMIN: 200,
  MEMBER: 403,
  AGENT_API_KEY: 403,
  NATIVE_INSTALLATION: 401,
  REMOVED_MEMBER: 403,
  SUSPENDED_USER: 401,
  UNJOINED_WORKSPACE: 403,
  FOREIGN_ID: 'N/A',
  UNAUTHENTICATED: 401,
};
const consoleResource: ConsoleResult = { ...consoleCollection, FOREIGN_ID: 404 };

export const CONNECTION_BETA_CONSOLE_AUTHORIZATION_MATRIX = {
  setup: consoleCollection,
  listRequests: consoleCollection,
  reviewRequest: consoleResource,
  approveRequest: { ...consoleResource, OWNER: 201, ADMIN: 201 },
  rejectRequest: consoleResource,
  listConnections: consoleCollection,
  readConnection: consoleResource,
  enableConnection: consoleResource,
  disableConnection: consoleResource,
  revokeConnection: consoleResource,
} as const;

/**
 * These factories are both serializers and an explicit allowlist: callers may
 * hand them richer persistence aggregates, but only the closed beta view can
 * cross the API boundary.
 */
export function createConnectionBetaDetail(input: unknown): ConnectionBetaViewResponse {
  const value = input as Record<string, unknown>;
  const connection = value.connection as Record<string, unknown>;
  const trust = value.trust as Record<string, unknown>;
  const instructions = value.instructions as Record<string, unknown>;
  const successor = value.capability === 'REMOTE_MCP_VERSIONED_SKILLS_V1';
  if (successor) assertClaudeValueLoopScopes(value.requestedScopes as string[]);
  return {
    connection: {
      id: String(connection.id),
      name: String(connection.name),
      kind: 'REMOTE_MCP',
      environment: connection.environment === null ? null : String(connection.environment),
      lifecycleStatus:
        connection.lifecycleStatus as ConnectionBetaViewResponse['connection']['lifecycleStatus'],
    },
    trust: {
      clientId: String(trust.clientId),
      clientName: String(trust.clientName),
      state: trust.state as ConnectionTrustViewState,
      registrationEvidence: 'PRE_REGISTERED_BETA_CLIENT',
      redirectUris: Array.isArray(trust.redirectUris)
        ? trust.redirectUris.map((uri) => String(uri))
        : [],
      requestedAt: trust.requestedAt === null ? null : String(trust.requestedAt),
    },
    capability: successor
      ? 'REMOTE_MCP_VERSIONED_SKILLS_V1'
      : 'REMOTE_MCP_OAUTH_BETA_SKILLS_READ_V1',
    ...(successor
      ? {
          requestedScopes: value.requestedScopes as ClaudeValueLoopScope[],
          consentRevision: 1 as const,
          enabled: value.enabled === true,
          writesEnabled: value.writesEnabled === true,
        }
      : {}),
    currentUserAuthority: value.currentUserAuthority as ConnectionCurrentUserAuthority,
    allowedTransitions: Array.isArray(value.allowedTransitions)
      ? (value.allowedTransitions as ConnectionAllowedTransition[])
      : [],
    version: Number(value.version),
    instructions: {
      serverUrl: String(instructions.serverUrl),
      clientName: String(instructions.clientName),
    },
  };
}

export function createConnectionBetaListItem(input: unknown): ConnectionBetaListResponse {
  const value = input as Record<string, unknown>;
  const meta = value.meta as Record<string, unknown>;
  return {
    data: Array.isArray(value.data)
      ? value.data.map((entry) => createConnectionBetaDetail(entry))
      : [],
    meta: {
      page: Number(meta.page),
      pageSize: Number(meta.pageSize),
      total: Number(meta.total),
    },
  };
}

type ConnectionBetaMatrixDecision = 'ALLOW' | 'DENY';
type ConnectionBetaMatrixRow = Record<
  'OWNER' | 'ADMIN' | 'MEMBER' | 'AGENT' | 'REMOVED' | 'CROSS_WORKSPACE' | 'UNAUTHENTICATED',
  ConnectionBetaMatrixDecision
>;
const connectionBetaMatrix = (
  allowed: ReadonlyArray<keyof ConnectionBetaMatrixRow>
): ConnectionBetaMatrixRow => ({
  OWNER: allowed.includes('OWNER') ? 'ALLOW' : 'DENY',
  ADMIN: allowed.includes('ADMIN') ? 'ALLOW' : 'DENY',
  MEMBER: allowed.includes('MEMBER') ? 'ALLOW' : 'DENY',
  AGENT: allowed.includes('AGENT') ? 'ALLOW' : 'DENY',
  REMOVED: allowed.includes('REMOVED') ? 'ALLOW' : 'DENY',
  CROSS_WORKSPACE: allowed.includes('CROSS_WORKSPACE') ? 'ALLOW' : 'DENY',
  UNAUTHENTICATED: allowed.includes('UNAUTHENTICATED') ? 'ALLOW' : 'DENY',
});

export const CONNECTION_BETA_AUTHORIZATION_MATRIX = {
  bind: connectionBetaMatrix(['OWNER', 'ADMIN']),
  list: connectionBetaMatrix(['OWNER', 'ADMIN']),
  detail: connectionBetaMatrix(['OWNER', 'ADMIN']),
  revokeOwnGrant: connectionBetaMatrix(['OWNER', 'ADMIN', 'MEMBER']),
  invoke: connectionBetaMatrix(['OWNER', 'ADMIN', 'MEMBER']),
} as const;

/**
 * Phase 3D.0A's API and gateway share this tiny, JTI-only invalidation
 * protocol. It deliberately carries no tenant, user, token, or credential
 * metadata: the gateway always rechecks the authoritative database tuple.
 */
export const CONNECTION_AUTHORITY_JTI_DENYLIST_PREFIX = 'connection-authority:jti:';
export const CONNECTION_AUTHORITY_INTROSPECTION_INVALIDATE_CHANNEL =
  'connection-authority:introspection-invalidate';

/**
 * Rolling-deployment bridge for gateway replicas released before Phase 3D.0A
 * renamed the invalidation protocol. Keep these values in the shared runtime
 * so the API publisher and the new gateway use the exact old wire contract.
 * Removal requires a coordinated deployment once no legacy replicas remain.
 */
export const LEGACY_GATEWAY_JTI_DENYLIST_PREFIX = 'gw:jti:';
export const LEGACY_GATEWAY_INTROSPECTION_INVALIDATE_CHANNEL = 'gw:cache:invalidate';

export function connectionAuthorityJtiDenylistKey(jti: string): string {
  return `${CONNECTION_AUTHORITY_JTI_DENYLIST_PREFIX}${jti}`;
}

export function legacyGatewayJtiDenylistKey(jti: string): string {
  return `${LEGACY_GATEWAY_JTI_DENYLIST_PREFIX}${jti}`;
}

export function connectionAuthorityInvalidationMessage(jti: string): string {
  return JSON.stringify({ type: 'connection-authority-jti', jti });
}

export function legacyGatewayInvalidationMessage(jti: string): string {
  return JSON.stringify({ type: 'introspection', jti });
}

export type ConnectionIdentity =
  | { status: 'mapped'; connectionId: string; connectionName: string }
  | { status: 'unavailable' };

export interface ApiKeyValidationResponse {
  agentId: number;
  agentName: string;
  workspaceId: number;
  connectionIdentity?: ConnectionIdentity;
}

export const CONNECTION_DEVICE_AUTHORIZATION_STATUSES = [
  'PENDING',
  'APPROVED',
  'DENIED',
  'CREDENTIAL_ISSUED',
  'CONSUMED',
  'EXPIRED',
] as const;
export type ConnectionDeviceAuthorizationStatus =
  (typeof CONNECTION_DEVICE_AUTHORIZATION_STATUSES)[number];

export const CONNECTION_SETUP_GRANT_STATUSES = [
  'PENDING',
  'CONSUMED',
  'REVOKED',
  'EXPIRED',
] as const;
export type ConnectionSetupGrantStatus = (typeof CONNECTION_SETUP_GRANT_STATUSES)[number];

export const DEVICE_INSTALLABLE_CONNECTION_KINDS = ['CLI', 'LOCAL_MCP'] as const;
export type DeviceInstallableConnectionKind = (typeof DEVICE_INSTALLABLE_CONNECTION_KINDS)[number];

export const CONNECTION_DEVICE_AUTHORIZATION_TTL_SECONDS = 600;
export const CONNECTION_DEVICE_POLL_INTERVAL_SECONDS = 5;
export const CONNECTION_DEVICE_USER_CODE_LENGTH = 8;
export const CONNECTION_PENDING_CREDENTIAL_TTL_SECONDS = 300;
export const CONNECTION_ROTATION_CONFIRMATION_TTL_SECONDS = 300;
export const CONNECTION_ACTIVE_CREDENTIAL_TTL_SECONDS = 90 * 24 * 60 * 60;
export const CONNECTION_DEVICE_USER_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' as const;
export const CONNECTION_NATIVE_TOKEN_PATTERN =
  /^skci_w([1-9][0-9]{0,9})_([A-Za-z0-9_-]{11})\.([A-Za-z0-9_-]{43})$/;

export const CONNECTION_DEVICE_POLL_ERRORS = [
  'authorization_pending',
  'slow_down',
  'access_denied',
  'expired_token',
  'credential_already_issued',
] as const;
export const CONNECTION_DEVICE_INSTALLATION_ERRORS = [
  'invalid_device_code',
  'authorization_pending',
  'slow_down',
  'access_denied',
  'expired_token',
  'credential_already_issued',
  'authorization_already_consumed',
  'duplicate_installation_header',
  'installation_credential_invalid',
  'stale_installation_epoch',
  'authorization_expired',
  'credential_expired',
  'installation_kind_mismatch',
  'installation_not_active',
  'native_product_scopes_forbidden',
  'rotation_already_pending',
  'rotation_not_found',
  'rotation_expired',
  'rotation_confirmation_invalid',
] as const;
export type ConnectionDeviceInstallationErrorIdentifier =
  (typeof CONNECTION_DEVICE_INSTALLATION_ERRORS)[number];

/**
 * Native installation product authority is intentionally closed-world.  This
 * is separate from the persistence enum: LEGACY_COMPAT remains historical
 * vocabulary and is never an installation product grant.
 */
export const CONNECTION_PRODUCT_SCOPES = ['skills.read'] as const;
export type ConnectionProductScope = (typeof CONNECTION_PRODUCT_SCOPES)[number];

type DeviceInstallationPrincipal =
  | 'OWNER'
  | 'ADMIN'
  | 'MEMBER'
  | 'LEGACY_AGENT'
  | 'NATIVE_INSTALLATION'
  | 'UNAUTHENTICATED';
type DeviceInstallationDecision = 'ALLOW' | 'DENY';
type DeviceInstallationAuthorizationRow = Record<
  DeviceInstallationPrincipal,
  DeviceInstallationDecision
>;
const all = (
  allowed: readonly DeviceInstallationPrincipal[]
): DeviceInstallationAuthorizationRow => ({
  OWNER: allowed.includes('OWNER') ? 'ALLOW' : 'DENY',
  ADMIN: allowed.includes('ADMIN') ? 'ALLOW' : 'DENY',
  MEMBER: allowed.includes('MEMBER') ? 'ALLOW' : 'DENY',
  LEGACY_AGENT: allowed.includes('LEGACY_AGENT') ? 'ALLOW' : 'DENY',
  NATIVE_INSTALLATION: allowed.includes('NATIVE_INSTALLATION') ? 'ALLOW' : 'DENY',
  UNAUTHENTICATED: allowed.includes('UNAUTHENTICATED') ? 'ALLOW' : 'DENY',
});
const EVERY_PRINCIPAL: DeviceInstallationPrincipal[] = [
  'OWNER',
  'ADMIN',
  'MEMBER',
  'LEGACY_AGENT',
  'NATIVE_INSTALLATION',
  'UNAUTHENTICATED',
];
export const CONNECTION_DEVICE_AUTHORIZATION_MATRIX = {
  start: all(EVERY_PRINCIPAL),
  poll: all(EVERY_PRINCIPAL),
  confirm: all(['OWNER', 'ADMIN', 'MEMBER', 'NATIVE_INSTALLATION']),
  create_connection: all(['OWNER', 'ADMIN']),
  decide: all(['OWNER', 'ADMIN']),
  issue_setup_grant: all(['OWNER', 'ADMIN']),
  complete_setup_grant: all(['OWNER', 'ADMIN', 'MEMBER']),
  read_metadata: all(['OWNER', 'ADMIN']),
  admin_revoke: all(['OWNER', 'ADMIN']),
  own_identity: all(['NATIVE_INSTALLATION']),
  rotate_start: all(['NATIVE_INSTALLATION']),
  rotate_confirm: all(['NATIVE_INSTALLATION']),
  own_revoke: all(['NATIVE_INSTALLATION']),
} as const;

export interface ConnectionDeviceAuthorizationStartRequest {
  clientKind: DeviceInstallableConnectionKind;
  clientName?: string;
}
export interface ConnectionDeviceAuthorizationStartResponse {
  deviceCode: string;
  userCode: string;
  verificationUri: string;
  verificationUriComplete: string;
  expiresIn: 600;
  interval: 5;
}
export interface ConnectionCreateRequest {
  name: string;
  kind: DeviceInstallableConnectionKind;
  environment?: string;
}
export interface ConnectionDeviceAuthorizationDecisionRequest {
  decision: 'APPROVE' | 'DENY';
}
export interface ConnectionDeviceAuthorizationDecisionResponse {
  status: 'APPROVED' | 'DENIED';
  connectionId: string;
  expiresAt: string;
}
export interface ConnectionSetupGrantIssueRequest {
  assignedUserId: number;
  expiresIn?: number;
}
export interface ConnectionSetupGrantIssueResponse {
  grantCode: string;
  connectionId: string;
  assignedUserId: number;
  expiresAt: string;
}
export interface ConnectionSetupGrantCompleteRequest {
  grantCode: string;
  userCode: string;
}
export interface ConnectionDeviceAuthorizationPollRequest {
  deviceCode: string;
}
export interface ConnectionDeviceAuthorizationConfirmRequest {
  deviceCode: string;
}
export interface ConnectionDeviceAuthorizationPollResponse {
  status?: 'credential_issued';
  connectionId: string;
  installationId: string;
  credentialId: string;
  credential: string;
  installationProof: string;
  credentialExpiresAt: string;
  confirmationExpiresAt: string;
}
export interface ConnectionInstallationIdentityResponse {
  workspaceId: number;
  connectionId: string;
  connectionName: string;
  kind: DeviceInstallableConnectionKind;
  installationId: string;
  credentialId: string;
  credentialPrefix: string;
  installationEpoch: number;
  lifecycleStatus: 'ACTIVE';
  scopes: ConnectionProductScope[];
  issuedAt: string;
  expiresAt: string | null;
}
export interface ConnectionInstallationCredentialMetadata {
  credentialId: string;
  installationId: string;
  lifecycleStatus: 'PENDING' | 'ACTIVE' | 'DISABLED' | 'REVOKED';
  credentialPrefix: string;
  installationEpoch: number;
  scopes: ConnectionProductScope[];
  issuedAt: string;
  activatedAt: string | null;
  expiresAt: string | null;
  lastAuthenticatedAt: string | null;
  revokedAt: string | null;
}
export interface ConnectionInstallationRevokeRequest {
  expectedEpoch: number;
}

export interface ConnectionInstallationRotationStartRequest {
  expectedEpoch: number;
}
export interface ConnectionInstallationRotationStartResponse {
  rotationId: string;
  predecessorCredentialId: string;
  successorCredentialId: string;
  installationId: string;
  successorEpoch: number;
  lifecycleStatus: 'PENDING';
  credential: string;
  installationProof: string;
  confirmationExpiresAt: string;
}
export interface ConnectionInstallationRotationConfirmRequest {
  rotationId: string;
  expectedEpoch: number;
}
export type ConnectionInstallationRotationConfirmResponse = ConnectionInstallationIdentityResponse;

export interface ConnectionInstallationScopeReplaceRequest {
  expectedEpoch: number;
  scopes: ConnectionProductScope[];
}

export interface ConnectionInstallationScopeResponse {
  credentialId: string;
  installationId: string;
  installationEpoch: number;
  /** Observation only; callers must use expectedEpoch for replacement. */
  version: number;
  scopes: ConnectionProductScope[];
  updatedAt: string;
}

type ConnectionProductPrincipal =
  | 'OWNER'
  | 'ADMIN'
  | 'MEMBER'
  | 'LEGACY_AGENT'
  | 'NATIVE_INSTALLATION'
  | 'UNAUTHENTICATED';
type ConnectionProductDecision = 'ALLOW' | 'DENY';
type ConnectionProductAuthorizationRow = Record<
  ConnectionProductPrincipal,
  ConnectionProductDecision
>;

const productAll = (
  allowed: readonly ConnectionProductPrincipal[]
): ConnectionProductAuthorizationRow => ({
  OWNER: allowed.includes('OWNER') ? 'ALLOW' : 'DENY',
  ADMIN: allowed.includes('ADMIN') ? 'ALLOW' : 'DENY',
  MEMBER: allowed.includes('MEMBER') ? 'ALLOW' : 'DENY',
  LEGACY_AGENT: allowed.includes('LEGACY_AGENT') ? 'ALLOW' : 'DENY',
  NATIVE_INSTALLATION: allowed.includes('NATIVE_INSTALLATION') ? 'ALLOW' : 'DENY',
  UNAUTHENTICATED: allowed.includes('UNAUTHENTICATED') ? 'ALLOW' : 'DENY',
});

export interface ConnectionSkillListParams {
  cursor?: string;
  limit?: number;
  search?: string;
}

export interface ConnectionSkillCatalogueItem {
  id: number;
  name: string;
  description: string | null;
  category: { id: number; name: string } | null;
  updatedAt: string;
}

export type ConnectionSkillListResponse = PaginatedResponse<ConnectionSkillCatalogueItem, string>;

export interface ConnectionSkillDetailResponse extends ConnectionSkillCatalogueItem {
  parentId: number | null;
  improvementGuidelines: string | null;
}

export const CONNECTION_PRODUCT_AUTHORIZATION_MATRIX = {
  replace_scopes: productAll(['OWNER', 'ADMIN']),
  list_skills: productAll(['NATIVE_INSTALLATION']),
  get_skill: productAll(['NATIVE_INSTALLATION']),
} as const;
